import type { RateRow } from "./types";

/**
 * Look up the **mature** actuarial rate for a state code.
 *
 * Returns `null` when the code isn't in the table so the UI can
 * render "—" — we deliberately don't fall back to a sibling row
 * because silently substituting (e.g. "CA" when "CA - ROS" was
 * meant) would mask data-entry errors and inflate premium.
 *
 * The mature column is the steady-state actuarial rate for facilities
 * that have been on policy long enough to age past the year1–year4
 * ramp. Most quoted accounts land here. Year-1-through-4 lookups will
 * arrive later as a separate function once UW asks for them.
 */
export function lookupMatureRate(code: string, rates: RateRow[]): number | null {
    const trimmed = code.trim();
    if (!trimmed) return null;
    const row = rates.find((r) => r.code === trimmed);
    return row ? row.mature : null;
}

/**
 * Compose the Rates lookup key from a state code + an urban/ROS flag.
 *
 * Urban facilities key off the bare state code ("CA"); rest-of-state
 * facilities key off the suffixed code ("CA - ROS"). The SoT for a
 * facility stores `state` and `geo` separately so this composition
 * stays at the call site — it makes the urban/ROS choice explicit
 * and reviewable.
 *
 * `geo` is intentionally permissive: only the literal "ros" triggers
 * the suffix. Anything else ("urban", "", `undefined`, `null`)
 * returns the bare code.
 */
export function composeRateCode(state: string, geo?: string | null): string {
    const s = state.trim();
    if (!s) return "";
    return geo === "ros" ? `${s} - ROS` : s;
}
